import type { ColumnType } from 'antd/es/table';

import { sortByTime } from 'utils/antd/sorters';
import { formatDate } from 'utils/dayjs';
import { hasValue } from 'utils/hasValue';

import type { OrderReports } from 'api';

import { COLLECTION_ORDER_STATUSES } from 'models/order';

import type { FindAccountByAccountNumber } from 'hooks/context/useVirtualAccounts';

import { ColumnTitle } from '@atoms/ColumnTitle';
import { DownloadThinIcon } from '@atoms/Icon/DownloadThinIcon';
import { StatusTag } from '@atoms/StatusTag';

import { FormattedDateTime } from '@molecules/FormattedDateTime';

export const getColumns = (
  findAccountByAccountNumber: FindAccountByAccountNumber,
  downloadReport: (report: OrderReports) => void
): ColumnType<OrderReports>[] => {
  return [
    {
      key: 'empty_space',
      width: 50
    },
    {
      title: <ColumnTitle title="From" />,
      dataIndex: 'created_after',
      key: 'created_after',
      render: (createdAfter: string) => (
        <div className="text-lg">{formatDate(createdAfter)}</div>
      )
    },
    {
      title: <ColumnTitle title="To" />,
      dataIndex: 'created_before',
      key: 'created_before',
      render: (createdBefore: string) => (
        <div className="text-lg">{formatDate(createdBefore)}</div>
      )
    },
    {
      title: <ColumnTitle title="Created date (UTC)" />,
      dataIndex: 'created_at',
      key: 'created_at',
      render: (createdAt: string) => <FormattedDateTime value={createdAt} />,
      sorter: (a, b) => sortByTime(a.created_at, b.created_at),
      onHeaderCell: () => ({
        className: 'align-sorter-icon'
      })
    },
    {
      title: <ColumnTitle title="Account" />,
      dataIndex: 'virtual_account_number',
      key: 'virtual_account_number',
      align: 'right',
      render: (virtualAccountNumber?: string) => {
        const account = hasValue(virtualAccountNumber)
          ? findAccountByAccountNumber(virtualAccountNumber)
          : undefined;

        return (
          <div>
            <p className="text-sm">{virtualAccountNumber}</p>
            <div className="font-semibold">
              {account?.name ?? 'All accounts'}
            </div>
          </div>
        );
      }
    },
    {
      title: <ColumnTitle title="Status" />,
      dataIndex: 'status',
      key: 'status',
      align: 'right',
      render: (status?: string | null) =>
        hasValue(status) ? (
          <StatusTag status={status} statuses={COLLECTION_ORDER_STATUSES} />
        ) : (
          ''
        )
    },
    {
      title: <ColumnTitle title="Action" />,
      key: 'action',
      align: 'center',
      render: (_, report) => {
        return (
          <div className="align-center flex h-full justify-center">
            <DownloadThinIcon
              className="cursor-pointer"
              fontSize="32px"
              onClick={() => downloadReport(report)}
            />
          </div>
        );
      }
    }
  ];
};
